import { useEffect, useState } from 'react'
// eslint-disable-next-line import/no-extraneous-dependencies
import axios from 'axios'
import { toast } from 'react-toastify'
import {
  getMixCategorias,
  getMixClientesPorCategoria,
  getMixConversaoVendedor,
  getMixRankingCategorias,
  getMixRankingClientes,
  getMixSimulador,
} from '../../services/MixService/getMixAdmin'
import { getVendedoresAll } from '../../services/SalesPeople/getSalesPeople'

type CategoriaSelecionada = { categoria: string; segmento: string } | null

// Chave da aba "Clientes por potencial". O agregado dela e o mais caro da tela e a aba padrao e
// outra, entao o fetch (e a carga de vendedores) so acontece com ela aberta.
const ABA_CLIENTES = 'clientes'

// PD-0002 Mix Inteligente (Fase 4) - palco admin. Ranking/potencial por categoria + consulta
// invertida (clientes que recebem a categoria) + ranking de clientes por potencial a agregar.
// So leitura de agregados (o motor e batch).
export function useMixAdmin() {
  const [estrategia, setEstrategia] = useState<MixEstrategiaType>('SEGMENTO')
  const [ranking, setRanking] = useState<GetMixRankingCategoriasType>(
    {} as GetMixRankingCategoriasType
  )
  const [clientes, setClientes] = useState<GetMixClientesPorCategoriaType>(
    {} as GetMixClientesPorCategoriaType
  )
  const [selecionada, setSelecionada] = useState<CategoriaSelecionada>(null)
  const [isLoadingRanking, setIsLoadingRanking] = useState(false)
  const [isLoadingClientes, setIsLoadingClientes] = useState(false)
  const [pageRanking, setPageRanking] = useState(1)
  const [pageClientes, setPageClientes] = useState(1)

  // Aba "Clientes": ranking por potencial a agregar, com filtro opcional de vendedor.
  const [rankingClientes, setRankingClientes] =
    useState<GetMixRankingClientesType>({} as GetMixRankingClientesType)
  const [isLoadingRankingClientes, setIsLoadingRankingClientes] =
    useState(false)
  const [pageRankingClientes, setPageRankingClientes] = useState(1)
  const [abaAtiva, setAbaAtiva] = useState('oportunidades')
  const [vendedorId, setVendedorId] = useState<number | null>(null)
  const [listaVendedores, setListaVendedores] = useState<GetCompanieAll>([])

  // Filtros de atividade comercial da aba "Clientes".
  //
  // O padrao de 30 dias vive AQUI e nao no backend (que trata ausente como "sem filtro"): assim o
  // contrato segue aditivo e o frontend antigo nao passa a receber lista recortada na janela de
  // deploy. null = "Sem filtro" no Select, e nao "campo vazio".
  //
  // Escolher 30 dias por padrao esconde a maioria da lista: na base de referencia, 287 dos 1.210
  // clientes recomendados compraram nos ultimos 30 dias, e 659 nunca compraram nada (cold-start,
  // que por construcao nao passam neste filtro). E o recorte pedido - "nao me traga cliente
  // perdido" -, e a tela diz isso em texto em vez de escondê-lo.
  const [compraUltimosDias, setCompraUltimosDias] = useState<number | null>(30)
  const [predicaoProximosDias, setPredicaoProximosDias] = useState<
    number | null
  >(null)

  // Simulador de campanha: categorias disponiveis (multi-select), selecao, conversao (%) e projecao.
  const [categoriasDisponiveis, setCategoriasDisponiveis] = useState<string[]>(
    []
  )
  const [categoriasSelecionadas, setCategoriasSelecionadas] = useState<
    string[]
  >([])
  const [conversao, setConversao] = useState(10)
  const [simulacao, setSimulacao] = useState<MixSimuladorType | null>(null)

  // Conversao do vendedor (Fase 5, nivel 2) - independente da estrategia/filtros; carrega uma vez.
  const [conversaoVendedor, setConversaoVendedor] =
    useState<MixConversaoType | null>(null)

  useEffect(() => {
    const source = axios.CancelToken.source()
    const buscar = async () => {
      const result = await getMixConversaoVendedor(source)
      if ('message' in result) {
        if (result.message !== 'cancelToken') {
          toast.error(result.message)
        }
      } else {
        setConversaoVendedor(result)
      }
    }
    buscar()
    return () => source.cancel('cancelToken')
  }, [])

  // Lista de vendedores do seletor (mesma fonte de Previsoes / Vendas / Predict Analytics: o
  // endpoint ja respeita o escopo de quem consulta). So carrega quando a aba "Clientes por
  // potencial" e aberta - quem fica em "Oportunidades" nao paga a chamada. Sem cancelToken porque
  // getVendedoresAll nao aceita um (assinatura usada por 3 telas).
  useEffect(() => {
    if (abaAtiva !== ABA_CLIENTES || listaVendedores.length > 0) {
      return
    }
    const carregarVendedores = async () => {
      const result = await getVendedoresAll('')
      if (Array.isArray(result)) {
        setListaVendedores(result)
      } else {
        toast.error(result.message)
      }
    }
    carregarVendedores()
  }, [abaAtiva])

  const fetchRanking = async (source: any) => {
    setIsLoadingRanking(true)
    const result = await getMixRankingCategorias(
      source,
      estrategia,
      '',
      pageRanking
    )
    if ('message' in result) {
      if (result.message === 'cancelToken') {
        return
      }
      toast.error(result.message)
    } else {
      setRanking(result)
    }
    setIsLoadingRanking(false)
  }

  const fetchClientes = async (source: any) => {
    if (!selecionada) {
      setClientes({} as GetMixClientesPorCategoriaType)
      return
    }
    setIsLoadingClientes(true)
    const result = await getMixClientesPorCategoria(
      source,
      selecionada.categoria,
      estrategia,
      selecionada.segmento,
      pageClientes
    )
    if ('message' in result) {
      if (result.message === 'cancelToken') {
        return
      }
      toast.error(result.message)
    } else {
      setClientes(result)
    }
    setIsLoadingClientes(false)
  }

  const fetchRankingClientes = async (source: any) => {
    setIsLoadingRankingClientes(true)
    const result = await getMixRankingClientes(
      source,
      estrategia,
      '',
      vendedorId,
      pageRankingClientes,
      compraUltimosDias,
      predicaoProximosDias
    )
    if ('message' in result) {
      if (result.message === 'cancelToken') {
        return
      }
      toast.error(result.message)
    } else {
      setRankingClientes(result)
    }
    setIsLoadingRankingClientes(false)
  }

  useEffect(() => {
    const source = axios.CancelToken.source()
    fetchRanking(source)
    return () => source.cancel('cancelToken')
  }, [JSON.stringify({ estrategia, pageRanking })])

  useEffect(() => {
    const source = axios.CancelToken.source()
    fetchClientes(source)
    return () => source.cancel('cancelToken')
  }, [JSON.stringify({ selecionada, estrategia, pageClientes })])

  // So busca com a aba aberta: e o agregado mais caro da tela (GROUP BY + COUNT(DISTINCT) sobre
  // mix_recomendacao) e a aba padrao e "Oportunidades".
  useEffect(() => {
    if (abaAtiva !== ABA_CLIENTES) {
      return undefined
    }
    const source = axios.CancelToken.source()
    fetchRankingClientes(source)
    return () => source.cancel('cancelToken')
  }, [
    abaAtiva,
    estrategia,
    vendedorId,
    pageRankingClientes,
    compraUltimosDias,
    predicaoProximosDias,
  ])

  // Lista de categorias do simulador (por estrategia).
  const fetchCategorias = async (source: any) => {
    const result = await getMixCategorias(source, estrategia, '')
    if (Array.isArray(result)) {
      setCategoriasDisponiveis(result)
    } else if (result.message !== 'cancelToken') {
      toast.error(result.message)
    }
  }

  // Projecao do simulador para as categorias selecionadas.
  const fetchSimulacao = async (source: any) => {
    if (categoriasSelecionadas.length === 0) {
      setSimulacao(null)
      return
    }
    const result = await getMixSimulador(
      source,
      estrategia,
      categoriasSelecionadas,
      ''
    )
    if ('message' in result) {
      if (result.message === 'cancelToken') {
        return
      }
      toast.error(result.message)
    } else {
      setSimulacao(result)
    }
  }

  useEffect(() => {
    const source = axios.CancelToken.source()
    fetchCategorias(source)
    return () => source.cancel('cancelToken')
  }, [estrategia])

  useEffect(() => {
    const source = axios.CancelToken.source()
    fetchSimulacao(source)
    return () => source.cancel('cancelToken')
  }, [JSON.stringify({ estrategia, categoriasSelecionadas })])

  // Trocar de estrategia reinicia os rankings e limpa a categoria selecionada (chaves por modo).
  const onChangeEstrategia = (value: MixEstrategiaType) => {
    setEstrategia(value)
    setPageRanking(1)
    setPageRankingClientes(1)
    setSelecionada(null)
    setCategoriasSelecionadas([])
  }

  const selecionarCategoria = (categoria: string, segmento: string) => {
    setSelecionada({ categoria, segmento })
    setPageClientes(1)
  }

  // Trocar de vendedor muda o conjunto: a paginacao do ranking de clientes volta ao inicio.
  const onChangeVendedor = (value: number | null) => {
    setVendedorId(value)
    setPageRankingClientes(1)
  }

  // Mesmo motivo dos demais filtros: sem o reset, a pagina 7 sobre um conjunto novo (bem menor)
  // volta vazia com totalElements alto, e a tela parece quebrada.
  const onChangeCompraUltimosDias = (value: number | null) => {
    setCompraUltimosDias(value)
    setPageRankingClientes(1)
  }

  const onChangePredicaoProximosDias = (value: number | null) => {
    setPredicaoProximosDias(value)
    setPageRankingClientes(1)
  }

  return {
    estrategia,
    onChangeEstrategia,
    ranking,
    clientes,
    selecionada,
    selecionarCategoria,
    isLoadingRanking,
    isLoadingClientes,
    pageRanking,
    setPageRanking,
    pageClientes,
    setPageClientes,
    abaAtiva,
    setAbaAtiva,
    rankingClientes,
    isLoadingRankingClientes,
    pageRankingClientes,
    setPageRankingClientes,
    vendedorId,
    onChangeVendedor,
    listaVendedores,
    compraUltimosDias,
    onChangeCompraUltimosDias,
    predicaoProximosDias,
    onChangePredicaoProximosDias,
    categoriasDisponiveis,
    categoriasSelecionadas,
    setCategoriasSelecionadas,
    conversao,
    setConversao,
    simulacao,
    conversaoVendedor,
  }
}
